<script lang="ts">
	// Formatters/i18n
	import { format } from 'src/services/i18n';

	// Svelte
	import { onDestroy, onMount } from 'svelte';
	import { params, push } from 'svelte-spa-router';

	// Types
	import type { InputProperties } from 'src/types/InputProperties';
	import type { CloudApiResponse } from 'src/types/responses/CloudApiResponse';

	// App Components
	import PasswordUtility from 'src/components/PasswordUtility.svelte';
	import SignUpConsentCheckboxes from 'src/components/Profiles/signup/SignUpConsentCheckboxes.svelte';
	import SignUpLoginPrompt from 'src/components/Profiles/signup/SignUpLoginPrompt.svelte';

	// Design system components
	import { Button, Input, Text, toast } from '@Shelly/shelly-design-system/index';

	// Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	import { normalizeEmail } from 'src/helpers/email_helpers';
	import {
		DEFAULT_SIGNUP_PASSWORD_INPUT_PROPS,
		validateSignUpConsents,
		validateSignUpPassword,
	} from 'src/helpers/signup_helpers';

	// Services
	import { postReq } from 'src/services/http';
	import jwtDecode from 'jwt-decode';

	let email: string;
	let passwordError: string;

	let isLoading = false;
	let termsCheck = false;
	let privacyPolicyCheck = false;
	let newsletterCheck = false;

	let passwordInputProps: InputProperties = { ...DEFAULT_SIGNUP_PASSWORD_INPUT_PROPS };

	$: passwordInputProps.disabled = isLoading;

	onMount(() => {
		if (!$params.token || !$params.email) {
			navigateToLogin();
			return;
		}

		jwtDecode($params.token);
		email = $params.email;
	});

	onDestroy(() => {
		cleanUp();
	});

	function handleSignUp(): void {
		email = normalizeEmail(email);

		if (!validateForm()) {
			return;
		}

		isLoading = true;

		const path = `auth/complete_subaccount_registration`;
		const body = {
			email: email,
			password: passwordInputProps.value,
		};
		const options = {
			Authorization: `Bearer ${$params.token}`,
		};

		postReq(path, body, 10000, null, false, false, options)
			.then(handleSuccessfullRegister)
			.catch(handleRegisterError)
			.finally(() => {
				isLoading = false;
			});
	}

	function handleSuccessfullRegister(response: CloudApiResponse): void {
		if (!response.isok) {
			throw new Error('Something went wrong');
		}

		toast.open({
			type: 'success',
			title: $format('register_success'),
			duration: 5000,
		});

		navigateToLogin();
	}

	function handleRegisterError(): void {
		toast.open({
			type: 'danger',
			duration: 2000,
			title: $format('error_global', {
				default: 'Something went wrong!',
			}),
		});
	}

	function navigateToLogin(): void {
		push('/login');
	}

	function validateForm(): boolean {
		passwordError = validateSignUpPassword(passwordInputProps.value, $format);
		if (passwordError !== '') {
			return false;
		}

		return validateSignUpConsents(termsCheck, privacyPolicyCheck, $format);
	}

	function clearErrors(): void {
		passwordError = '';
	}

	function cleanUp(): void {
		isLoading = false;
		termsCheck = false;
		privacyPolicyCheck = false;
		newsletterCheck = false;
		passwordInputProps.value = '';
		email = '';
	}
</script>

<Text element="h1" class="mb-l">
	{$format('sub_account_sign_up_header', {
		default: 'You have been invited to join Shelly.',
	})}
</Text>

<Input
	id="input-sub-signup-email"
	aria-labelledby="input-sub-signup-email"
	size="l"
	class="mb-m"
	disabled={true}
	bind:value={email}
	label={capFirstChar($format('placeholder_email', { default: 'enter your e-mail' }))}
	icon="fas fa-envelope"
	type="email"
/>

<PasswordUtility
	bind:inputProps={passwordInputProps}
	bind:error={passwordError}
	on:focus={clearErrors}
	on:enter={handleSignUp}
/>

<SignUpConsentCheckboxes
	idPrefix="sub-account"
	bind:termsCheck
	bind:privacyPolicyCheck
	bind:newsletterCheck
	disabled={isLoading}
	showDataAct={true}
	newsletterClass="mb-s"
/>

<Button
	class="text-capitalize"
	variant="primary"
	width="full"
	disabled={!termsCheck || !privacyPolicyCheck}
	loading={isLoading}
	on:click={handleSignUp}
>
	{$format('button_sign_up')}
</Button>

<SignUpLoginPrompt disabled={isLoading} on:loginClick={navigateToLogin} />
